import { Injectable, inject } from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {catchError, map, mergeMap, of, switchMap, withLatestFrom} from 'rxjs';
import {DeviceType} from '@birco/rainmesh-api';
import {DEVICE_STATE_SERVICE_INJECTION_TOKEN, DeviceStateService} from '../../../core/services/api/device-state-service.interface';
import {selectPumpFilter} from './pump.selectors';
import {Store} from '@ngrx/store';
import {PumpActions} from './pump.actions';
import {DeviceActions} from '../../devices/state/device.actions';
import {DeviceUpdateRequest} from '../../devices/models/device-response.interface';

@Injectable()
export class PumpEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private deviceStateService = inject<DeviceStateService>(DEVICE_STATE_SERVICE_INJECTION_TOKEN);

  public updateFilter$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PumpActions.public.updatePumpFilter),
      map((_props) => PumpActions.private.loadPumpsWithFilters())
    );
  });

  public loadPumps$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PumpActions.private.loadPumpsWithFilters),
      withLatestFrom(this.store.select(selectPumpFilter)),
      mergeMap(([_action, filter]) => {
        return this.deviceStateService.getDevices({...filter, deviceTypes: [DeviceType.PUMP]}).pipe(
          map((devicesResponse) => PumpActions.private.loadPumpsSuccess({devicesResponseDto: devicesResponse})),
          catchError((error) => of(PumpActions.private.loadPumpsFailure({error})))
        )
      }),
    );
  });

  public triggerPumpAction$ = createEffect(() => this.actions$.pipe(
    ofType(PumpActions.public.triggerPumpAction),
    switchMap((props) => {
      return this.deviceStateService.triggerDeviceAction({
        id: props.id,
        value: props.value,
      }).pipe(
        map((responseDto) => PumpActions.private.triggerPumpActionSuccess({
          responseDto: responseDto,
        })),
        catchError((error) => of(PumpActions.private.triggerPumpActionFailure({
          id: props.id,
          attemptedValue: props.value,
          error: error,
        }))),
      );
    }),
  ));

  public updatePump$ = createEffect(() => this.actions$.pipe(
    ofType(PumpActions.public.updatePump),
    switchMap((props) => {
      const {type: _actionType, ...updatePayload} = props;
      const update: DeviceUpdateRequest = {...updatePayload};

      return this.deviceStateService.updateDevice(update).pipe(
        map((responseDto) => PumpActions.private.updatePumpSuccess({
          responseDto: responseDto,
        })),
        catchError((error) => of(PumpActions.private.updatePumpFailure({
          id: props.id,
          error: error,
        }))),
      );
    }),
  ));

  public loadConnectedValves$ = createEffect(() => this.actions$.pipe(
    ofType(PumpActions.public.loadConnectedValves),
    switchMap((props) => {
      return this.deviceStateService.getConnectedValves({pumpId: props.pumpId}).pipe(
        map((responseDto) => PumpActions.private.loadConnectedValvesSuccess({
          pumpId: props.pumpId,
          devicesResponseDto: responseDto,
        })),
        catchError((error) => of(PumpActions.private.loadConnectedValvesFailure({
          pumpId: props.pumpId,
          error: error,
        }))),
      );
    }),
  ));

  public replaceConnectedValves$ = createEffect(() => this.actions$.pipe(
    ofType(PumpActions.public.replaceConnectedValves),
    switchMap((props) => {
      return this.deviceStateService.replaceConnectedValves({
        pumpId: props.pumpId,
        connectedValveIds: props.connectedValveIds,
      }).pipe(
        map((responseDto) => PumpActions.private.replaceConnectedValvesSuccess({
          pumpId: props.pumpId,
          devicesResponseDto: responseDto,
        })),
        catchError((error) => of(PumpActions.private.replaceConnectedValvesFailure({
          pumpId: props.pumpId,
          error: error,
        }))),
      );
    }),
  ));

  // The replace response only contains the valves, so refetch the pump for its connectedValveIds.
  public refreshPumpAfterReplaceConnectedValves$ = createEffect(() => this.actions$.pipe(
    ofType(PumpActions.private.replaceConnectedValvesSuccess),
    map((props) => DeviceActions.public.loadDeviceById({id: props.pumpId})),
  ));
}
